import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';

// Estado inicial da autenticação
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Actions do reducer
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Reducer para gerenciar estado da autenticação
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload,
      };
    
    default:
      return state;
  }
}

// Contexto de autenticação
const AuthContext = createContext();

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider do contexto de autenticação
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const syncInFlightRef = useRef(false);

  const syncSession = useCallback(
    async ({ silent = false } = {}) => {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        if (!silent) {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
        return;
      }

      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;

      if (!silent) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      }

      try {
        // A chamada a /me pode retornar 401; o interceptor do apiClient faz refresh e re-tenta.
        const user = await authService.getCurrentUser();
        const currentToken = localStorage.getItem('token') || savedToken;

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token: currentToken },
        });
      } catch (error) {
        // Sessão inválida/expirada
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } finally {
        if (!silent) {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
        syncInFlightRef.current = false;
      }
    },
    []
  );

  // Verificar token salvo ao inicializar
  useEffect(() => {
    syncSession({ silent: false });
  }, [syncSession]);

  // Revalidar sessão quando o usuário volta para a aba/janela.
  // Isso evita telas de permissão sendo renderizadas antes do refresh terminar.
  useEffect(() => {
    const onFocusOrVisible = () => {
      const hasToken = !!localStorage.getItem('token');
      const hasRefresh = !!localStorage.getItem('refreshToken');

      if (!hasToken || !hasRefresh) return;
      // Só revalida quando a aba está visível (evita spam em background)
      if (document.visibilityState && document.visibilityState !== 'visible') return;

      syncSession({ silent: true });
    };

    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    return () => {
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
    };
  }, [syncSession]);

  // Se o apiClient sinalizar sessão expirada, fazer logout de forma limpa.
  useEffect(() => {
    const onSessionExpired = () => {
      logout();
    };

    window.addEventListener('auth:sessionExpired', onSessionExpired);
    return () => {
      window.removeEventListener('auth:sessionExpired', onSessionExpired);
    };
  }, []);

  // Função de login
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const response = await authService.login(email, password);
      
      // Salvar tokens no localStorage
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      
      // Buscar dados do usuário
      const user = await authService.getCurrentUser();
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token: response.access_token }
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao fazer login';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  };

  // Função de logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Função para limpar erros
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Função para atualizar dados do usuário
  const updateUser = async (userData) => {
    try {
      const updatedUser = await authService.updateCurrentUser(userData);
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: updatedUser
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao atualizar usuário';
      return { success: false, error: errorMessage };
    }
  };

  // Função para alterar senha
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao alterar senha';
      return { success: false, error: errorMessage };
    }
  };

  // Função para renovar token automaticamente
  const refreshToken = async () => {
    try {
      const savedRefreshToken = localStorage.getItem('refreshToken');
      if (!savedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(savedRefreshToken);
      
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: state.user, token: response.access_token }
      });
      
      return response.access_token;
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Verificar se o usuário é admin
  const isAdmin = () => {
    return state.user?.is_admin || false;
  };

  // Verificar se o usuário tem uma permissão específica
  const hasPermission = (permission) => {
    if (!state.user || !state.user.permissions) {
      return false;
    }
    
    // Se for admin (flag) ou superadmin (role), tem todas as permissões
    if (state.user.is_admin || state.user.role === 'superadmin') {
      return true;
    }
    
    // Verificar se a permissão está na lista
    const permissions = Array.isArray(state.user.permissions) 
      ? state.user.permissions 
      : [];
    
    return permissions.includes(permission);
  };

  // Verificar se possui ao menos uma das permissões
  const hasAnyPermission = (permissionsAny) => {
    if (!Array.isArray(permissionsAny) || permissionsAny.length === 0) return false;
    return permissionsAny.some((perm) => hasPermission(perm));
  };

  // Verificar se o usuário tem um dos papéis especificados
  const hasRole = (roles) => {
    if (!state.user || !state.user.role) return false;
    if (Array.isArray(roles)) {
      return roles.includes(state.user.role);
    }
    return state.user.role === roles;
  };

  // Verificar se é super administrador
  const isSuperAdmin = () => {
    return state.user?.role === 'superadmin';
  };

  // Verificar se tem acesso a módulo específico
  const hasModuleAccess = (module) => {
    // Se for superadmin ou admin, tem acesso a tudo
    if (isSuperAdmin() || isAdmin()) {
      return true;
    }

    const modulePermissions = {
      'users': ['users.read', 'users.create', 'users.update', 'users.delete', 'users.manage'],
      'finance': [
        'finance.transactions.read', 'finance.transactions.read.all', 'finance.transactions.read.self',
        'finance.transactions.create', 'finance.transactions.update', 'finance.transactions.delete', 
        'finance.transactions.verify',
        'finance.dashboard.read', 'finance.reports.read', 'analytics.finance.read'
      ],
      'inventory': [
        'inventory.assets.read', 'inventory.assets.create', 'inventory.assets.update', 'inventory.assets.delete', 
        'inventory.assets.manage', 'analytics.inventory.read'
      ],
      'analytics': ['analytics.dashboard.read', 'analytics.reports.read', 'analytics.data.upload', 'analytics.manual_sales_performance.read'],
      'rbac': ['roles.read', 'roles.create', 'roles.update', 'roles.delete', 'roles.manage'],
      'system': ['system.config']
    };

    const permissions = modulePermissions[module] || [];
    return permissions.some(permission => hasPermission(permission));
  };

  // Valor do contexto
  const value = {
    ...state,
    login,
    logout,
    syncSession,
    clearError,
    updateUser,
    changePassword,
    refreshToken,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isSuperAdmin,
    hasModuleAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
